'use client'
import * as React from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { X, ArrowRight, ClipboardCheck } from 'lucide-react'

// Timed lead magnet. After a quiet delay on any page, a small corner
// card slides in offering the free UGC audit. Dismissing it snoozes
// the card for a week (localStorage), so it never nags. It sits
// bottom-left so it never collides with the bottom-right chat widget.

const STORAGE_KEY = 'idukki:leadmagnet:ugc-audit'
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000
const DELAY_MS = 35_000

export function LeadMagnetPopup() {
  const pathname = usePathname()
  const [visible, setVisible] = React.useState(false)
  const [shown, setShown] = React.useState(false)

  React.useEffect(() => {
    // Don't pitch the audit to someone already on the audit page.
    if (pathname?.startsWith('/tools/ugc-audit')) return
    let dismissedAt = 0
    try { dismissedAt = Number(localStorage.getItem(STORAGE_KEY)) || 0 } catch {}
    if (Date.now() - dismissedAt < SNOOZE_MS) return
    const t = setTimeout(() => {
      setVisible(true)
      requestAnimationFrame(() => setShown(true))
    }, DELAY_MS)
    return () => clearTimeout(t)
  }, [pathname])

  function close() {
    setShown(false)
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())) } catch {}
    setTimeout(() => setVisible(false), 250)
  }

  if (!visible) return null

  return (
    <div
      role="dialog"
      aria-label="Free UGC audit"
      className={`fixed bottom-4 left-4 right-4 z-40 transition-[opacity,transform] duration-300 sm:right-auto sm:w-[340px] ${
        shown ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
      }`}
    >
      <div className="relative overflow-hidden rounded-2xl border bg-card p-5 shadow-2xl shadow-black/10">
        <button
          type="button"
          onClick={close}
          aria-label="Dismiss"
          className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition hover:bg-secondary hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <ClipboardCheck className="h-5 w-5" />
        </span>
        <p className="mt-3 text-[11px] font-medium uppercase tracking-[0.16em] text-primary">Free · 2 minutes</p>
        <h3 className="mt-1 font-display text-lg font-semibold tracking-tight">
          How healthy is your UGC programme?
        </h3>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Score how you collect, clear rights for, display and convert customer content, and see where you&rsquo;re leaking revenue.
        </p>
        <Link
          href="/tools/ugc-audit"
          onClick={close}
          className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
        >
          Take the free audit <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  )
}
